import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/ui-primitives";
import { Printer } from "lucide-react";
import { PhotoThumbs } from "@/components/PhotoUpload";

export default function Reports() {
  const [pm, setPm] = useState([]);
  const [faults, setFaults] = useState([]);
  const [equipment, setEquipment] = useState([]);

  const load = async () => {
    try {
      const [pmRes, fRes, eqRes] = await Promise.all([
        api.get("/pm-records"),
        api.get("/faults"),
        api.get("/equipment"),
      ]);
      setPm(pmRes.data);
      setFaults(fRes.data);
      setEquipment(eqRes.data);
    } catch { toast.error("Failed to load"); }
  };

  useEffect(() => { load(); }, []);

  const equipmentLabel = (id) => {
    const e = equipment.find((x) => x.id === id);
    return e ? `${e.type} · ${e.asset_id}` : "—";
  };

  return (
    <div className="p-8 max-w-[1800px] mx-auto">
      <PageHeader eyebrow="Documentation" title="Service" accent="Reports">
        <button
          data-testid="print-btn"
          onClick={() => window.print()}
          className="px-5 py-2.5 bg-[#007AFF] hover:bg-[#3395FF] text-white text-sm font-medium flex items-center gap-2"
        >
          <Printer className="w-4 h-4" /> Print This Page
        </button>
      </PageHeader>

      <p className="text-sm text-[#A3A3A3] mb-6">
        Compiled view of all PM records and fault reports. Use the print button or export data from the sidebar.
      </p>

      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <div className="font-mono-tab text-[10px] tracking-[0.2em] uppercase text-[#737373] mb-3">
            PM Records ({pm.length})
          </div>
          <div data-testid="pm-report-list" className="space-y-3">
            {pm.length === 0 && (
              <div className="border border-white/10 bg-[#121212] p-6 text-center text-[#A3A3A3] text-sm">No PM records.</div>
            )}
            {pm.map((p) => (
              <div key={p.id} className="border border-white/10 bg-[#121212] p-5">
                <div className="flex items-start justify-between mb-2">
                  <div className="font-display font-bold text-white">PM: {equipmentLabel(p.equipment_id)}</div>
                  <div className="font-mono-tab text-xs text-[#E0E0E0]">{p.date}</div>
                </div>
                <div className="font-mono-tab text-[11px] tracking-wider text-[#737373] uppercase mb-3">
                  {p.interval} · {p.tech || "—"} ·{" "}
                  <span className="text-[#22C55E]">{p.pass_count} P</span> ·{" "}
                  <span className="text-[#FF3B30]">{p.fail_count} F</span> ·{" "}
                  <span className="text-[#A3A3A3]">{p.na_count} N/A</span>
                </div>
                {p.notes && (<div className="text-sm text-[#E0E0E0] mb-3 whitespace-pre-wrap">{p.notes}</div>)}
                {p.checklist?.length > 0 && (
                  <div className="border-t border-white/5 pt-3 grid grid-cols-1 sm:grid-cols-2 gap-1">
                    {p.checklist.map((c, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-[#A3A3A3] truncate">{c.task}</span>
                        <span className={`font-mono-tab ml-2 ${
                          c.status === "PASS" ? "text-[#22C55E]" :
                          c.status === "FAIL" ? "text-[#FF3B30]" : "text-[#737373]"
                        }`}>{c.status}</span>
                      </div>
                    ))}
                  </div>
                )}
                <PhotoThumbs photos={p.attachments} size={16} />
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="font-mono-tab text-[10px] tracking-[0.2em] uppercase text-[#737373] mb-3">
            Fault Reports ({faults.length})
          </div>
          <div data-testid="fault-report-list" className="space-y-3">
            {faults.length === 0 && (
              <div className="border border-white/10 bg-[#121212] p-6 text-center text-[#A3A3A3] text-sm">No fault reports.</div>
            )}
            {faults.map((f) => (
              <div key={f.id} className="border border-white/10 bg-[#121212] p-5">
                <div className="flex items-start justify-between mb-2">
                  <div className="font-display font-bold text-white">Fault: {equipmentLabel(f.equipment_id)}</div>
                  <span className={`inline-block px-2 py-0.5 text-[10px] font-mono-tab tracking-[0.15em] uppercase border ${
                    f.status === "Open"
                      ? "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/30"
                      : "bg-[#22C55E]/15 text-[#22C55E] border-[#22C55E]/30"
                  }`}>
                    {f.status}
                  </span>
                </div>
                <div className="font-mono-tab text-[11px] tracking-wider text-[#737373] uppercase mb-3">{f.date}</div>
                <div className="text-sm text-[#E0E0E0] mb-2"><span className="text-[#737373]">Fault:</span> {f.fault}</div>
                {f.cause && <div className="text-sm text-[#E0E0E0] mb-1"><span className="text-[#737373]">Cause:</span> {f.cause}</div>}
                {f.repair && <div className="text-sm text-[#E0E0E0] mb-1"><span className="text-[#737373]">Repair:</span> {f.repair}</div>}
                {f.parts && <div className="text-sm text-[#E0E0E0]"><span className="text-[#737373]">Parts:</span> {f.parts}</div>}
                <PhotoThumbs photos={f.attachments} size={16} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
